import React, { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Copy, Check, RefreshCw, Send, Link } from 'lucide-react';
import toast from 'react-hot-toast';
import { Link as RouterLink } from 'react-router-dom';

type TradingBot = {
  id: string;
  webhook_url: string;
};

interface BotWebhookManagerProps {
  bot: TradingBot;
  onUpdate?: () => void;
  isAdmin?: boolean;
}

export default function BotWebhookManager({ bot, onUpdate, isAdmin = false }: BotWebhookManagerProps) {
  if (!isAdmin) {
    return null;
  }

  const [copied, setCopied] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [testing, setTesting] = useState(false);

  const getWebhookUrl = () => {
    // Use production domain
    return `https://aix.iccc.co/api/tradingview-webhook?bot=${bot.id}&key=${bot.webhook_url}`;
  };

  const handleCopyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(getWebhookUrl());
      setCopied(true);
      toast.success('Webhook URL copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy webhook URL');
    }
  };

  const handleRegenerateWebhook = async () => {
    try {
      setRegenerating(true);
      const newWebhookKey = crypto.randomUUID();
      
      const { error } = await supabase
        .from('trading_bots')
        .update({ 
          webhook_url: newWebhookKey,
          updated_at: new Date().toISOString()
        })
        .eq('id', bot.id);

      if (error) throw error;
      
      toast.success('Webhook URL regenerated successfully');
      onUpdate?.();
    } catch (error: any) {
      toast.error('Failed to regenerate webhook URL');
      console.error('Error:', error.message);
    } finally {
      setRegenerating(false);
    }
  };

  const handleTestWebhook = async () => {
    try {
      setTesting(true);
      const response = await fetch(getWebhookUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          exchange: 'binance',
          ticker: 'BTCUSDT',
          price: '50000',
          message: 'Test signal from TradingView'
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === 'ok') {
        toast.success('Webhook test successful');
      } else {
        throw new Error('Test failed: ' + result.message);
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to test webhook');
      console.error('Error testing webhook:', error);
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-lg font-medium text-white">TradingView Webhook</h4>
        <div className="flex items-center space-x-2">
          <RouterLink
            to="/faq#tradingview"
            className="text-cyan-400 hover:text-cyan-300 transition-colors"
            title="View Documentation"
          >
            <Link className="h-5 w-5" />
          </RouterLink>
          <button
            onClick={handleTestWebhook}
            disabled={testing}
            className={`p-2 text-cyan-400 hover:text-cyan-300 transition-colors ${
              testing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Test webhook"
          >
            <Send className={`h-5 w-5 ${testing ? 'animate-pulse' : ''}`} />
          </button>
          <button
            onClick={handleCopyWebhook}
            className="p-2 text-cyan-400 hover:text-cyan-300 transition-colors"
            title="Copy webhook URL"
          >
            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
          </button>
          <button
            onClick={handleRegenerateWebhook}
            disabled={regenerating}
            className={`p-2 text-cyan-400 hover:text-cyan-300 transition-colors ${
              regenerating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="Regenerate webhook URL"
          >
            <RefreshCw className={`h-5 w-5 ${regenerating ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-black/20 rounded-lg p-3">
        <code className="text-sm text-gray-300 break-all font-mono">
          {getWebhookUrl()}
        </code>
      </div>
    </div>
  );
}
